import i18n from '@shared/i18n';
import { lowerFirst } from 'lodash-es';
import {
	ENodeInputTypeCode,
	ENodeIODataTypeCode,
	ENodeOutputTypeCode,
	INodeData,
	INodeInnerData,
	ITextGenForm,
} from '@shared/typings/flow/common/data';
import { IBuildFormNode } from '@shared/typings/build/classes/node';

import { getFlowId } from '../id';
import { InvalidTypeError } from './error';
import { isProd } from '@shared/env';

// 返回小写 output
export const getDefaultOutputTitleLower = () => {
	return lowerFirst(i18n.get('Workflow_NodeDetail_LLM_Output'));
};

/**
 * @description 获取默认的文本生成器面板的数据
 */
export function getDefaultTextGenerationData({
	defaultOutputItem,
}: {
	defaultOutputItem: INodeInnerData.OutputText;
}): ITextGenForm.Form {
	return {
		prompt: { id: getFlowId(), text: '', inputs: [] },
		images: [],
		outputConfig: [
			{
				id: defaultOutputItem.id,
				title: `${getDefaultOutputTitleLower()} 1`,
				description: '',
				defaultContent: null,
			},
		],
	};
}

export function getDefaultVideoGenerationData(): INodeData.OutputVideo['videoInfo'] {
	return {
		prompt: {
			id: getFlowId(),
			text: '',
			inputs: [],
		},
		selectAudioId: '',
		useAi: true,
		duration: '5',
		frames: [
			{
				id: getFlowId(),
				imageUrl: '',
				path: '',
			},
		],
		audios: [],
		action: {
			selectId: null,
		},
		actions: [],
	};
}

// 获取长视频默认数据
export function getDefaultLVNodeVideoInfo(): INodeData.OutputLongVideo['mergeInfo'] {
	const selectAudioId = isProd ? '2020777178386104321' : '2020791196540235777';
	const audios = isProd
		? [
				{
					id: '2020777178386104321',
					name: 'Rise',
					filePath: 'assets/user/upload/audio/83f592f9-9cda-4469-ac81-1183c6248538.mp3',
				},
			]
		: [
				{
					id: '2020791196540235777',
					name: 'Rise（test2）',
					filePath: 'assets/user/upload/audio/2c744112-e797-4433-8436-ae8eb4216041.mp3',
				},
			];
	return {
		selectAudioId,
		seq: [
			{
				id: getFlowId(),
				imageUrl: '',
				path: '',
			},
			{
				id: getFlowId(),
				imageUrl: '',
				path: '',
			},
		],
		audios,
	};
}

export function createEmptyImageItemData(
	options?: Partial<ITextGenForm.Image>,
): ITextGenForm.Image {
	const defaultVal = {
		id: getFlowId(),
		path: '',
		imageUrl: '',
		title: 'image',
	};

	if (options) Object.assign(defaultVal, options);

	return defaultVal;
}

/**
 * @description 获取默认的outputItem标题
 * @param outputs outputs输出项数组
 */
export const getDefaultOutputTitle = (outputs: ITextGenForm.OutputItem[]) => {
	let max = 0;
	const defaultText = getDefaultOutputTitleLower();
	outputs.forEach((item) => {
		const title = item.title;
		// const regExp = new RegExp(`^output ([0-9]{1,5})$`);
		const regExp = new RegExp(`^${defaultText} ([0-9]{1,5})$`);
		const matched = title.match(regExp);
		if (!matched) return;

		max = Math.max(max, Number(matched[1]));
	});

	return `${defaultText} ${max + 1}`;
};

export function createEmptyOutputItemData(
	outputs: ITextGenForm.OutputItem[],
): ITextGenForm.OutputItem {
	return {
		id: `out_${getFlowId()}`,
		title: getDefaultOutputTitle(outputs),
		description: '',
		defaultContent: null,
	};
}

export const getDefaultOutput = <T extends INodeInnerData.UnionOutput>(
	type: INodeData.UnionData['type'],
	id: string,
	mixin?: Partial<INodeInnerData.UnionOutput>,
) => {
	const defaultOutputText = getDefaultOutputTitleLower();

	const output = {
		id,
	} as T;
	switch (type) {
		case ENodeInputTypeCode.Image:
		case ENodeOutputTypeCode.Image:
		case ENodeOutputTypeCode.Canvas: {
			Object.assign(
				output,
				{
					imageUrl: '',
					dataType: ENodeIODataTypeCode.Image,
					type: '10',
				},
				mixin,
			);
			return output as INodeInnerData.OutputImage;
		}
		case ENodeOutputTypeCode.Video:
		case ENodeOutputTypeCode.LongVideo: {
			Object.assign(
				output,
				{
					path: '',
					imageUrl: '',
					dataType: ENodeIODataTypeCode.Video,
					type: '30',
				},
				mixin,
			);
			return output as INodeInnerData.OutputVideo;
		}

		case ENodeInputTypeCode.Text:
		case ENodeOutputTypeCode.Text: {
			Object.assign(
				output,
				{
					text: '',
					dataType: ENodeIODataTypeCode.Text,
					type: '20',
					...(type === ENodeOutputTypeCode.Text ? { title: `${defaultOutputText} 1` } : {}),
				},
				mixin,
			);
			return output as INodeInnerData.OutputText;
		}

		default:
			throw InvalidTypeError(type);
	}
};

export const getDefaultInput = <T extends ENodeIODataTypeCode>(
	type: T,
	id: string,
	mixin?: Partial<INodeInnerData.UnionInput>,
) => {
	const input = {
		id,
		dataType: type,
		title: '',
	} as INodeInnerData.UnionInput;

	switch (type) {
		case ENodeIODataTypeCode.Image: {
			Object.assign(input, { imageUrl: '', type: '10' }, mixin);
			return input as INodeInnerData.InputImage;
		}

		case ENodeIODataTypeCode.Text: {
			Object.assign(input, { text: '', type: '20' }, mixin);
			return input as INodeInnerData.InputText;
		}

		case ENodeIODataTypeCode.Video: {
			Object.assign(input, { imageUrl: '', type: '30' }, mixin);
			return input as INodeInnerData.InputVideo;
		}

		default:
			throw InvalidTypeError(type);
	}
};

/**
 * @description 文本生成器右侧面板数据转化为节点的inputs
 * @param form
 * @returns
 */
export const transLLMData2Inputs = (form: ITextGenForm.Form) => {
	const inputs: INodeInnerData.UnionInput[] = [];
	form.prompt?.inputs?.length &&
		form.prompt?.inputs.forEach(({ id, title }) => {
			inputs.push(
				getDefaultInput(ENodeIODataTypeCode.Text, id, {
					title,
				}),
			);
		});

	form.images.length > 0 &&
		form.images.forEach(({ id, title, isUploading, imageUrl, path }) => {
			if (!isUploading) {
				const mixin: Partial<INodeInnerData.InputImage> = {
					title,
				};

				if (imageUrl) {
					Object.assign(mixin, {
						path,
						imageUrl,
					});
				}

				inputs.push(getDefaultInput(ENodeIODataTypeCode.Image, id, mixin));
			}
		});

	return inputs;
};

export const getOutputBydId = (id: string) => {
	return `out_${id}`;
};

type T_OptType = 'description' | 'default';
type T_Options = {
	['description']: Partial<IBuildFormNode.DescriptionData>;
	['default']: Partial<ITextGenForm.VarItem>;
};

type T_ReturnVal = {
	['description']: IBuildFormNode.DescriptionData;
	['default']: ITextGenForm.VarItem;
};

type IOptions<T extends T_OptType> = T_Options[T];
type IReturnVal<T extends T_OptType> = T_ReturnVal[T];

export const getDefaultVarData = <T extends T_OptType = 'default'>(
	type: T = 'default' as T,
	options?: IOptions<T>,
): IReturnVal<T> => {
	if (type === 'description') {
		const defaultVal = {
			id: '',
			color: '',
			title: '',
			description: '',
			userValue: '',
			value: '',
			tags: [''],
		};

		if (options) Object.assign(defaultVal, options);

		return defaultVal as IReturnVal<T>;
	}

	const defaultVal: ITextGenForm.VarItem = {
		id: '',
		color: '',
		title: '',
	};

	if (options) Object.assign(defaultVal, options);

	return defaultVal as IReturnVal<T>;
};
